import React from 'react';
import { Alert, AlertTitle, Box, Chip, Typography, LinearProgress } from '@mui/material';
import { useLiquidationRisk, LiquidationSeverity } from '../../hooks/useLiquidationRisk';
import { formatCurrency } from '../../utils/formatters';

/**
 * LiquidationRiskBanner — the "you are about to be liquidated, here is what to
 * add" strip that sits above the open-positions table.
 *
 * On 2026-09-08 six Delta positions were liquidated in the same second. This page
 * had been showing all six the whole afternoon with no indication that anything
 * was wrong, because the app had no concept of margin risk. This banner is the
 * in-app half of the fix; the other half is the Telegram ladder in the backend.
 *
 * Deliberately silent at SAFE and WATCH. A banner that is always on stops being
 * read, which is the same failure as a bot that is muted.
 */

const SEVERITY_UI: Record<LiquidationSeverity, { sev: 'info' | 'warning' | 'error'; label: string }> = {
  SAFE: { sev: 'info', label: 'Safe' },
  WATCH: { sev: 'info', label: 'Watch' },
  WARNING: { sev: 'warning', label: 'Warning' },
  CRITICAL: { sev: 'error', label: 'Critical' },
  EMERGENCY: { sev: 'error', label: 'Emergency' },
};

const LiquidationRiskBanner: React.FC = () => {
  const risk = useLiquidationRisk();

  // Nothing to show until the guard has actually evaluated. Note this is NOT the
  // same as "safe" — the tile in the dashboard cockpit renders the unknown state
  // explicitly. Here, staying quiet is right: an unknown state is not news.
  if (!risk?.available) return null;

  const severity = (risk.severity || 'SAFE') as LiquidationSeverity;
  if (severity === 'SAFE' || severity === 'WATCH') return null;

  const ui = SEVERITY_UI[severity];
  const mmr = risk.mmr;
  const target = risk.thresholds?.warning != null ? risk.thresholds.warning : 65;

  return (
    <Alert severity={ui.sev} sx={{ mb: 2, borderRadius: 2 }}>
      <AlertTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        Liquidation risk — {ui.label.toUpperCase()}
        <Chip
          size="small"
          label={mmr == null ? 'NO COLLATERAL' : `MMR ${mmr.toFixed(0)}% of 100%`}
          color={ui.sev === 'error' ? 'error' : 'warning'}
        />
        {risk.stale && <Chip size="small" variant="outlined" color="error" label="STALE — cannot refresh" />}
      </AlertTitle>

      {mmr != null && (
        <Box sx={{ mb: 1.25, maxWidth: 460 }}>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, mmr))}
            color={ui.sev === 'error' ? 'error' : 'warning'}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" color="text.secondary">
            Your exchange liquidates every position when this bar reaches 100%.
            {risk.bufferPct != null && ` A ${risk.bufferPct.toFixed(1)}% adverse move gets there.`}
          </Typography>
        </Box>
      )}

      {mmr == null && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Your collateral is zero or negative. There is nothing backing your open positions.
        </Typography>
      )}

      {/* The actual point of the feature: a number to act on, not just a warning. */}
      {!!risk.fundsRequiredInr && risk.fundsRequiredInr > 0 && (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Add {formatCurrency(Math.ceil(risk.fundsRequiredInr), 'INR', 0)} (${(risk.fundsRequiredUsd || 0).toFixed(2)}) to return to a safe level
          {risk.reduceFraction != null && risk.reduceFraction > 0
            ? `, or close ${(risk.reduceFraction * 100).toFixed(0)}% of your position size.`
            : '.'}
        </Typography>
      )}

      {risk.reserveBreached && (
        <Typography variant="caption" display="block" sx={{ mt: 0.75 }}>
          Free margin is also below your portfolio reserve, so new manual trades are currently being rejected.
        </Typography>
      )}
      {risk.modelUnverified && (
        <Typography variant="caption" display="block" sx={{ mt: 0.75 }}>
          Our margin model no longer matches the figures your exchange reports — treat the numbers above as
          approximate, but do check the account.
        </Typography>
      )}
      {risk.partialCoverage && !!risk.excludedSymbols?.length && (
        <Typography variant="caption" display="block" sx={{ mt: 0.75 }}>
          Not included above: {risk.excludedSymbols.join(', ')} — these could not be modelled.
        </Typography>
      )}
      <Typography variant="caption" display="block" sx={{ mt: 0.75, opacity: 0.75 }}>
        Warning threshold {target}% · updates live · this alert never places or closes an order.
      </Typography>
    </Alert>
  );
};

export default LiquidationRiskBanner;
